import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCalendarTime } from "./CalendarTimeContext";

import { isInvoicingSessionCancelled } from "components/Dashboard/DoctorDashboard/utils/isInvoicingSessionCancelled";
import { useAppointmentsInDateRangeForMultipleUsers } from "contexts/appointments";
import { useAppointmentSeries } from "contexts/appointments/series";
import useFetchInvoicesForAppointments from "hooks/useFetchInvoicesForAppointments";
import useLocationParams from "hooks/useLocationParams";
import { useDispatch } from "react-redux";
import { showSessionDetails } from "store/calendarSidebarSlice";
import { useShowCancelledSessions } from "./ShowCancelledSessionContext";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
import { useUser } from "contexts/UserContext";

type Context = {
  appointments: AppointmentMetaData[];
  appointmentsInDateRangeError: string | null;
  show: boolean;
  setShow: (arg: boolean) => void;
  forceRefreshAppointments: () => void;
  visibleTeamMemberIds: string[];
  setVisibleTeamMemberIds: Dispatch<SetStateAction<string[]>>;
};
const CalendarAppointmentsContext = createContext<Context>({
  appointments: [],
  appointmentsInDateRangeError: null,
  show: true,
  setShow: () => null,
  forceRefreshAppointments: () => null,
  visibleTeamMemberIds: [],
  setVisibleTeamMemberIds: () => null,
});

type Props = { children: ReactNode };

export const CalendarAppointmentsContextProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const { getParam } = useLocationParams();
  const { user } = useUser();
  const { timeRange } = useCalendarTime();
  const { showCancelledSessions } = useShowCancelledSessions();

  const [reloadTimestamp, setReloadTimestamp] = useState(0);
  const { teamMembers, practiceOwnerOrchidUserId } = useTeamContext();

  const [visibleTeamMemberIds, setVisibleTeamMemberIds] = useState<string[]>(
    [],
  );

  // Initialize all team members as visible when team members are loaded
  useEffect(() => {
    if (!user?.sub) {
      return;
    }
    // if (
    //   [
    //     "9099c99b-ff90-4e39-8286-4e62cfb763cc",
    //     "b02d40d3-446d-4e54-a48d-f2c27cfb566a",
    //     "4b6c2e07-7cd0-4bc7-b383-87c18c353915",
    //     "d2fdbb95-918a-4297-b4e7-d28fd12ded9e",
    //     "4b548507-a212-4fe6-999a-61bb0b8c3bd0",
    //     "bb45c521-3a85-4457-bba3-8cb1a45406a7",
    //     "1d3f0961-b168-4091-80d8-1f2d9a525a36",
    //     "f90fa3cd-c65e-47d6-8bdd-ebee767e0dcd",
    //     "b359c8e0-c78a-4eb2-820b-a7ddbbb8ecb8",
    //   ].includes(user.sub)
    // ) {
    //   setVisibleTeamMemberIds([user.sub]);
    //   return;
    // }

    if (teamMembers.length > 0 && visibleTeamMemberIds.length === 0) {
      if (user.role === "PRACTICE_SCHEDULER") {
        const allMemberIds = teamMembers
          .filter((member) => member.role !== "PRACTICE_SCHEDULER") // hide practice scheduler schedule
          .map((member) => member.orchid_user_id);
        setVisibleTeamMemberIds(allMemberIds);
      } else {
        setVisibleTeamMemberIds([user.sub]);
      }
    }
  }, [teamMembers, visibleTeamMemberIds.length, user?.sub, user?.role]);

  const hasNoTeamMembers = !teamMembers || teamMembers.length === 0;

  const { appointmentsInDateRange, appointmentsInDateRangeError } =
    useAppointmentsInDateRangeForMultipleUsers({
      newestTimestamp: timeRange[1],
      oldestTimestamp: timeRange[0],
      reloadTimestamp,
      // Pass undefined when no team members - hook will query current user directly without on_behalf_of
      onBehalfOf: hasNoTeamMembers ? undefined : visibleTeamMemberIds,
    });

  const appointmentsInit = appointmentsInDateRange || [];

  const { appointments: appoinmentsWithSeries } = useAppointmentSeries({
    appointments: appointmentsInit,
    reloadTimestamp,
    onBehalfOf: practiceOwnerOrchidUserId,
  });

  const { appointmentsWithInvoices } = useFetchInvoicesForAppointments(
    appoinmentsWithSeries,
    practiceOwnerOrchidUserId,
  );

  const [show, setShow] = useState(true);

  const filteredSessions = useMemo(() => {
    // Filter by visible team members first
    const teamMemberFiltered =
      visibleTeamMemberIds.length > 0
        ? appointmentsWithInvoices.filter((appointment) =>
            visibleTeamMemberIds.includes(appointment.orchid_pro.cognito_sub),
          )
        : appointmentsWithInvoices;

    // can show all
    if (show && showCancelledSessions) {
      return teamMemberFiltered;
    }
    return teamMemberFiltered.filter((el) => {
      return (
        (showCancelledSessions &&
          (el.statusFlags.cancelledOrAbandoned ||
            isInvoicingSessionCancelled(el, null))) ||
        (show &&
          !el.statusFlags.cancelledOrAbandoned &&
          !isInvoicingSessionCancelled(el, null))
      );
    });
  }, [
    appointmentsWithInvoices,
    show,
    showCancelledSessions,
    visibleTeamMemberIds,
  ]);

  const hasOpenedHighlightedAppointment = useRef(false);
  useEffect(() => {
    const highlightedAppointmentId = getParam("session_id");
    if (!highlightedAppointmentId || hasOpenedHighlightedAppointment.current) {
      return;
    }

    const highlightedAppointment = appointmentsWithInvoices.find(
      (el) => el.appointment_id.toString() === highlightedAppointmentId,
    );

    if (!highlightedAppointment) {
      return;
    }

    hasOpenedHighlightedAppointment.current = true;
    dispatch(
      showSessionDetails({
        appointment: highlightedAppointment,
        nextStatus: null,
      }),
    );
  }, [appointmentsWithInvoices, dispatch, getParam]);

  const forceRefreshAppointments = () => setReloadTimestamp(Date.now());

  return (
    <CalendarAppointmentsContext.Provider
      value={{
        appointments: filteredSessions,
        appointmentsInDateRangeError: appointmentsInDateRangeError
          ? appointmentsInDateRangeError
          : null,
        show,
        setShow,
        forceRefreshAppointments,
        visibleTeamMemberIds,
        setVisibleTeamMemberIds,
      }}
    >
      {children}
    </CalendarAppointmentsContext.Provider>
  );
};

export const useCalendarAppointments = () =>
  useContext(CalendarAppointmentsContext);
